import { Component, OnDestroy, OnInit, inject, output } from '@angular/core';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { EXPENDITURE_RATE_LABELS } from '../../../../../../../core/text-db/project-calculation.text';
import { GroupedDropdownOption } from '../../../../../../../shared/data-types/dropdown-option';
import { GroupResult, groupBy } from '@progress/kendo-data-query';
import { take } from 'rxjs';
import { ExpenditureOptions } from '../../../../../../../core/constants-repository/project-calculation.constants';
import { recordToGroupedDropdownOptions } from '../../../../../../../shared/functions/dropdown-functions';
import { getEnumKeyByEnumValue } from '../../../../../../../shared/functions/enum-functions';
import { ProjectModel } from '../../../../../../../core/models/project/project.model';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { ExpenditureEntryService } from '../services/expenditure-entry.service';
import { ProjectDataService } from '../../../../state-and-data-handling/project-data.service';
import { SubSink } from 'subsink';
import { ProjectExpenditureBudgetTransformerService } from '../services/project-expenditure-budget-transformer.service';
import { ExpenditureRateEntry } from '../../../../../../../core/models/project/project-expenditure-entry.model';

@Component({
  selector: 'app-project-calculation-form-expenditure',
  templateUrl: './project-calculation-form-expenditure.component.html',
  styleUrls: ['./project-calculation-form-expenditure.component.scss'],
  standalone: false,
})
export class ProjectCalculationFormExpenditureComponent implements OnInit, OnDestroy {
  readonly selectedExpenditureRateOptionChanged$ = output<ExpenditureOptions>();
  public projectExpenditureForm: FormGroup;
  public expenditureRateOptions: GroupedDropdownOption[] | GroupResult[];
  public selectedExpenditureRateOption: ExpenditureOptions;
  public ExpenditureRateOptions = ExpenditureOptions;
  public readonly faPenToSquare = faPenToSquare;
  public expenditureValue: number;
  public expenditureHistory: ExpenditureRateEntry[] = null;
  private readonly subs = new SubSink();

  // DI
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly projectDataService = inject(ProjectDataService);
  private readonly projectExpenditureBudgetTransformer = inject(
    ProjectExpenditureBudgetTransformerService,
  );
  private readonly expenditureEntryService: ExpenditureEntryService =
    inject(ExpenditureEntryService);

  ngOnInit(): void {
    const projectClone = this.projectDataService.getProjectClone();
    this.selectedExpenditureRateOption = projectClone.expenditureType;

    this.expenditureHistory =
      this.projectExpenditureBudgetTransformer.transformExpenditureEntriesExpenditureHourlyValueFixed(
        projectClone,
      );
    this.initExpenditureEntries(projectClone);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // The item of the expenditure rate option combobox has changed
  public expenditureRateOptionChanged($event: GroupedDropdownOption): void {
    const enumOption = getEnumKeyByEnumValue(ExpenditureOptions, $event.value.toString());
    this.selectedExpenditureRateOption = ExpenditureOptions[enumOption];
    this.projectExpenditureForm.controls.expenditureRateValue.patchValue(0);

    const projectClone = this.projectDataService.getProjectClone();

    projectClone.expenditureType = this.selectedExpenditureRateOption;

    this.expenditureHistory =
      this.selectedExpenditureRateOption === ExpenditureOptions.ExpendituresInHours
        ? []
        : [this.expenditureEntryService.createExpenditureEntry(0, projectClone.startsAt, 0)];
    this.expenditureValue = 0;

    projectClone.projectExpenditureEntries =
      this.projectExpenditureBudgetTransformer.transformHourlyFixedToExpenditureEntries(
        this.expenditureHistory,
      );

    // update cache
    this.projectDataService.updateProject(projectClone);
    // enable save button in footer
    this.selectedExpenditureRateOptionChanged$.emit(this.selectedExpenditureRateOption);
  }

  public openNewExpenditureRateDialog(): void {
    this.expenditureEntryService.openNewExpenditureRateDialog(
      this.expenditureHistory,
      (entries) => {
        this.expenditureHistory = entries;

        const projectClone = this.projectDataService.getProjectClone();
        projectClone.projectExpenditureEntries =
          this.projectExpenditureBudgetTransformer.transformHourlyFixedToExpenditureEntries(
            entries,
          );

        this.expenditureValue = entries[entries.length - 1]?.rate;
        this.projectDataService.updateProject(projectClone);
      },
    );
  }

  public isExpenditureHourlyValueFixed(): boolean {
    return this.selectedExpenditureRateOption === ExpenditureOptions.HourlyRateFixed;
  }

  private getSelectedOption(
    groupedDropdownOptions: GroupedDropdownOption[],
    project: ProjectModel,
  ): GroupedDropdownOption {
    return groupedDropdownOptions.find((option) => option.value === project.expenditureType);
  }

  private initExpenditureEntries(project: ProjectModel): void {
    recordToGroupedDropdownOptions(EXPENDITURE_RATE_LABELS)
      .pipe(take(1))
      .subscribe((groupedDropdownOptions: GroupedDropdownOption[]) => {
        this.expenditureRateOptions = groupBy(groupedDropdownOptions, [{ field: 'category' }]);

        this.projectExpenditureForm = this.formBuilder.group({
          expenditureRateType: [this.getSelectedOption(groupedDropdownOptions, project)],
          expenditureRateValue: [this.expenditureHistory[this.expenditureHistory.length - 1].rate],
        });

        this.expenditureValue = this.expenditureHistory[this.expenditureHistory.length - 1].rate;
      });
  }
}
